import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Switch,
  TextField,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import Autocomplete from '@material-ui/lab/Autocomplete';
import EmojiPicker, {
  EmojiStyle,
  SuggestionMode,
  Theme,
} from 'emoji-picker-react';
import emojiDataEs from 'emoji-picker-react/dist/data/emojis-es.json';
import Button from '../../components/common/Button';
import Alert from '../../components/common/Alert';
import CatalogApi from './catalog-api';

const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

const normalizeText = (value) =>
  typeof value === 'string' ? value.trim() : '';

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('No se pudo leer el banner.'));
    reader.readAsDataURL(file);
  });

const getOccasionLabel = (occasion) => {
  if (!occasion) {
    return '';
  }

  const emoji = normalizeText(occasion.emoji);
  const name = normalizeText(occasion.name);

  return emoji ? `${emoji} ${name}` : name;
};

const getOccasionEmoji = (occasion) =>
  occasion ? normalizeText(occasion.emoji) : '';

const buildPropagationMessage = (action, propagation) => {
  const count =
    Number(propagation && propagation.updatedPostulationsCount) || 0;

  if (!count) {
    return `Ocasión ${action} exitosamente.`;
  }

  const publicationLabel =
    count === 1 ? 'publicación asociada' : 'publicaciones asociadas';

  return `Ocasión ${action} exitosamente. Se sincronizó ${count} ${publicationLabel}.`;
};

const CatalogPageSettings = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [occasions, setOccasions] = useState([]);
  const [selectedOccasionId, setSelectedOccasionId] = useState('');
  const [name, setName] = useState('');
  const [emoji, setEmoji] = useState('');
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
  const [bannerFile, setBannerFile] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [bannerPreviewUrl, setBannerPreviewUrl] = useState('');
  const [activateOnCreate, setActivateOnCreate] = useState(true);
  const [isSavingOccasion, setIsSavingOccasion] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [occasionToDelete, setOccasionToDelete] = useState(null);
  const [formError, setFormError] = useState('');
  const [message, setMessage] = useState('');
  const [typeMessage, setTypeMessage] = useState('info');

  const selectedOccasion = useMemo(
    () =>
      occasions.find(
        (occasion) => String(occasion.id) === String(selectedOccasionId)
      ) || null,
    [occasions, selectedOccasionId]
  );

  const activeOccasion = useMemo(
    () => occasions.find((occasion) => Boolean(occasion.isActive)) || null,
    [occasions]
  );

  const isEditingOccasion = Boolean(selectedOccasion && selectedOccasion.id);
  const existingBannerPreviewUrl =
    (selectedOccasion &&
      (selectedOccasion.bannerImagePreviewUrl || selectedOccasion.bannerImage)) ||
    '';
  const formBannerPreviewUrl = bannerPreviewUrl || existingBannerPreviewUrl;

  const fetchOccasions = useCallback(async () => {
    try {
      setIsLoading(true);
      const {
        data: { occasions: catalogOccasions = [] },
      } = await CatalogApi.fetchOccasions();

      setOccasions(catalogOccasions);
      setSelectedOccasionId((currentSelectedOccasionId) => {
        const currentSelectionExists = catalogOccasions.some(
          (occasion) =>
            String(occasion.id) === String(currentSelectedOccasionId)
        );

        if (currentSelectionExists) {
          return currentSelectedOccasionId;
        }

        const activeCatalogOccasion =
          catalogOccasions.find((occasion) => Boolean(occasion.isActive)) ||
          null;

        return (activeCatalogOccasion && activeCatalogOccasion.id) || '';
      });
    } catch (error) {
      setMessage(
        (error && error.data && error.data.message) ||
          'Ocurrió un error al cargar las ocasiones del catálogo.'
      );
      setTypeMessage('error');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOccasions();
  }, [fetchOccasions]);

  useEffect(() => {
    setName((selectedOccasion && selectedOccasion.name) || '');
    setEmoji((selectedOccasion && selectedOccasion.emoji) || '');
    setBannerFile(null);
    setFileInputKey((currentKey) => currentKey + 1);
    setBannerPreviewUrl('');
    setActivateOnCreate(selectedOccasion ? Boolean(selectedOccasion.isActive) : true);
    setIsEmojiPickerOpen(false);
    setFormError('');
  }, [selectedOccasion]);

  useEffect(() => {
    return () => {
      if (bannerPreviewUrl) {
        URL.revokeObjectURL(bannerPreviewUrl);
      }
    };
  }, [bannerPreviewUrl]);

  const resetCreateForm = () => {
    setName('');
    setEmoji('');
    setBannerFile(null);
    setFileInputKey((currentKey) => currentKey + 1);
    setBannerPreviewUrl('');
    setActivateOnCreate(true);
    setIsEmojiPickerOpen(false);
    setFormError('');
  };

  const startCreatingOccasion = () => {
    setSelectedOccasionId('');
    resetCreateForm();
  };

  const handleBannerFileChange = (event) => {
    const file = (event.target.files && event.target.files[0]) || null;
    setFormError('');

    if (!file) {
      setBannerFile(null);
      setBannerPreviewUrl('');
      return;
    }

    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      setBannerFile(null);
      setBannerPreviewUrl('');
      event.target.value = '';
      setFormError('El banner debe ser una imagen JPG, PNG, WEBP o GIF.');
      return;
    }

    setBannerFile(file);
    setBannerPreviewUrl(URL.createObjectURL(file));
  };

  const handleEmojiClick = (emojiData) => {
    setEmoji(normalizeText(emojiData && emojiData.emoji));
    setIsEmojiPickerOpen(false);
  };

  const uploadBanner = async (file) => {
    const fileData = await readFileAsDataUrl(file);
    const {
      data: { objectKey, url },
    } = await CatalogApi.uploadOccasionBanner({
      fileName: file.name,
      fileType: file.type,
      fileData,
    });

    const bannerUrl = url || objectKey;

    if (!bannerUrl) {
      throw new Error('No se pudo subir el banner a S3.');
    }

    return bannerUrl;
  };

  const saveOccasion = async () => {
    if (isSavingOccasion) {
      return;
    }

    const normalizedName = normalizeText(name);
    const normalizedEmoji = normalizeText(emoji);

    if (!normalizedName || (!isEditingOccasion && !bannerFile)) {
      setFormError('Debes completar el nombre de la ocasión y el banner.');
      return;
    }

    if (normalizedEmoji && normalizedEmoji.length > 16) {
      setFormError('El emoji debe ser un símbolo corto.');
      return;
    }

    try {
      setIsSavingOccasion(true);
      setFormError('');
      const bannerImage = bannerFile ? await uploadBanner(bannerFile) : null;

      if (isEditingOccasion) {
        const payload = {
          name: normalizedName,
          emoji: normalizedEmoji,
        };

        if (bannerImage) {
          payload.bannerImage = bannerImage;
        }

        const {
          data: { occasion: updatedOccasion, propagation },
        } = await CatalogApi.updateOccasion(selectedOccasion.id, payload);

        setMessage(buildPropagationMessage('actualizada', propagation));
        setTypeMessage('success');
        await fetchOccasions();

        if (updatedOccasion && updatedOccasion.id) {
          setSelectedOccasionId(updatedOccasion.id);
        }

        return;
      }

      const {
        data: { occasion: createdOccasion },
      } = await CatalogApi.createOccasion({
        name: normalizedName,
        emoji: normalizedEmoji,
        bannerImage,
        isActive: activateOnCreate,
      });

      resetCreateForm();
      setMessage('Ocasión creada exitosamente.');
      setTypeMessage('success');
      await fetchOccasions();

      if (createdOccasion && createdOccasion.id) {
        setSelectedOccasionId(createdOccasion.id);
      }
    } catch (error) {
      const errorMessage =
        (error && error.data && error.data.message) ||
        (error && error.message) ||
        'Ocurrió un error al guardar la ocasión.';
      setFormError(errorMessage);
      setMessage(errorMessage);
      setTypeMessage('error');
    } finally {
      setIsSavingOccasion(false);
    }
  };

  const openDeleteModal = (occasion) => {
    setOccasionToDelete(occasion);
  };

  const closeDeleteModal = () => {
    if (isDeleting) {
      return;
    }

    setOccasionToDelete(null);
  };

  const deleteSelectedOccasion = async () => {
    if (isDeleting || !(occasionToDelete && occasionToDelete.id)) {
      return;
    }

    try {
      setIsDeleting(true);
      const { data } = await CatalogApi.deleteOccasion(occasionToDelete.id);
      setMessage(
        buildPropagationMessage('eliminada', data && data.propagation)
      );
      setTypeMessage('success');
      setSelectedOccasionId((currentSelectedOccasionId) =>
        String(currentSelectedOccasionId) === String(occasionToDelete.id)
          ? ''
          : currentSelectedOccasionId
      );
      setOccasionToDelete(null);
      await fetchOccasions();
    } catch (error) {
      setMessage(
        (error && error.data && error.data.message) ||
          'Ocurrió un error al eliminar la ocasión seleccionada.'
      );
      setTypeMessage('error');
    } finally {
      setIsDeleting(false);
    }
  };

  const activateSelectedOccasion = async () => {
    if (isActivating) {
      return;
    }

    if (!(selectedOccasion && selectedOccasion.id)) {
      setMessage('Debes seleccionar una ocasión existente.');
      setTypeMessage('warning');
      return;
    }

    try {
      setIsActivating(true);
      await CatalogApi.activateOccasion(selectedOccasion.id);
      setMessage('Ocasión activa actualizada exitosamente.');
      setTypeMessage('success');
      await fetchOccasions();
    } catch (error) {
      setMessage(
        (error && error.data && error.data.message) ||
          'Ocurrió un error al guardar la ocasión seleccionada.'
      );
      setTypeMessage('error');
    } finally {
      setIsActivating(false);
    }
  };

  const deactivateActiveOccasion = async () => {
    if (isDeactivating) {
      return;
    }

    if (!activeOccasion) {
      setMessage('El catálogo ya está sin ocasión activa.');
      setTypeMessage('info');
      return;
    }

    try {
      setIsDeactivating(true);
      await CatalogApi.deactivateOccasion();
      setSelectedOccasionId('');
      setMessage('Catálogo dejado sin ocasión activa exitosamente.');
      setTypeMessage('success');
      await fetchOccasions();
    } catch (error) {
      setMessage(
        (error && error.data && error.data.message) ||
          'Ocurrió un error al dejar el catálogo sin ocasión activa.'
      );
      setTypeMessage('error');
    } finally {
      setIsDeactivating(false);
    }
  };

  const renderBannerPreview = (occasion) => {
    const bannerUrl =
      (occasion && (occasion.bannerImagePreviewUrl || occasion.bannerImage)) ||
      '';

    if (!bannerUrl) {
      return (
        <div className="catalog-settings__empty-preview">
          Sin banner disponible
        </div>
      );
    }

    return (
      <img
        className="catalog-settings__banner-image"
        src={bannerUrl}
        alt={`Banner ${occasion.name || 'catálogo'}`}
      />
    );
  };

  return (
    <div className="catalog-settings">
      {message && <Alert type={typeMessage}>{message}</Alert>}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <section className="catalog-settings__panel">
            <div className="catalog-settings__section-header">
              <div className="catalog-settings__section-title-row">
                <h2>{isEditingOccasion ? 'Actualizar ocasión' : 'Crear ocasión'}</h2>
                {isEditingOccasion ? (
                  <button
                    type="button"
                    className="catalog-settings__create-new-button"
                    onClick={startCreatingOccasion}
                  >
                    Crear nueva ocasión
                  </button>
                ) : null}
              </div>
              <span>
                {isEditingOccasion
                  ? 'Actualiza el nombre o reemplaza el banner asociado a esta ocasión.'
                  : 'Crea una nueva ocasión y asóciala a un banner para el catálogo público.'}
              </span>
            </div>

            <div className="catalog-settings__form">
              <TextField
                className="catalog-settings__text-control"
                fullWidth
                variant="outlined"
                label="Nombre de la ocasión"
                placeholder="Ej: Navidad"
                value={name}
                onChange={(event) => setName(event.target.value)}
              />

              <div className="catalog-settings__emoji-field">
                <TextField
                  className="catalog-settings__text-control"
                  fullWidth
                  variant="outlined"
                  label="Emoji de la ocasión"
                  placeholder="Ej: 🎁"
                  value={emoji}
                  onChange={(event) => setEmoji(normalizeText(event.target.value))}
                  helperText="Elige desde el banco completo o pega un emoji manualmente."
                />
                <div className="catalog-settings__emoji-actions">
                  <button
                    type="button"
                    className="catalog-settings__emoji-picker-button"
                    onClick={() =>
                      setIsEmojiPickerOpen((currentValue) => !currentValue)
                    }
                  >
                    <span className="catalog-settings__emoji-button-symbol">
                      {emoji || '😀'}
                    </span>
                    <span>
                      {isEmojiPickerOpen
                        ? 'Cerrar banco de emojis'
                        : 'Elegir emoji'}
                    </span>
                  </button>
                  {emoji ? (
                    <button
                      type="button"
                      className="catalog-settings__emoji-clear-button"
                      onClick={() => setEmoji('')}
                    >
                      Quitar emoji
                    </button>
                  ) : null}
                </div>
                {isEmojiPickerOpen ? (
                  <div className="catalog-settings__emoji-picker">
                    <EmojiPicker
                      emojiData={emojiDataEs}
                      emojiStyle={EmojiStyle.NATIVE}
                      height={360}
                      lazyLoadEmojis
                      previewConfig={{ showPreview: false }}
                      searchClearButtonLabel="Limpiar busqueda"
                      searchPlaceholder="Buscar emoji"
                      suggestedEmojisMode={SuggestionMode.RECENT}
                      theme={Theme.LIGHT}
                      width="100%"
                      onEmojiClick={handleEmojiClick}
                    />
                  </div>
                ) : null}
              </div>

              <div className="catalog-settings__upload">
                <span className="catalog-settings__field-label">Banner</span>
                <input
                  key={fileInputKey}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleBannerFileChange}
                />
                {formBannerPreviewUrl ? (
                  <img
                    className="catalog-settings__banner-image"
                    src={formBannerPreviewUrl}
                    alt={
                      isEditingOccasion
                        ? `Banner ${selectedOccasion.name || 'catálogo'}`
                        : 'Vista previa del banner'
                    }
                  />
                ) : (
                  <div className="catalog-settings__empty-preview">
                    Selecciona una imagen para ver la vista previa.
                  </div>
                )}
              </div>

              {!isEditingOccasion ? (
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={activateOnCreate}
                      onChange={(event) =>
                        setActivateOnCreate(event.target.checked)
                      }
                    />
                  }
                  label="Dejar esta ocasión activa al crearla"
                />
              ) : null}

              {formError ? (
                <span className="catalog-settings__error">{formError}</span>
              ) : null}

              <div className="catalog-settings__actions">
                <Button
                  onClick={saveOccasion}
                  loading={isSavingOccasion}
                  disabled={isSavingOccasion}
                >
                  {isEditingOccasion ? 'Actualizar ocasión' : 'Crear ocasión'}
                </Button>
              </div>
            </div>
          </section>
        </Grid>

        <Grid item xs={12} md={6}>
          <section className="catalog-settings__panel">
            <div className="catalog-settings__section-header">
              <div className="catalog-settings__section-title-row">
                <h2>Elegir ocasión existente</h2>
                <button
                  type="button"
                  className="catalog-settings__create-new-button"
                  onClick={deactivateActiveOccasion}
                  disabled={isDeactivating || !activeOccasion}
                >
                  {isDeactivating ? 'Desactivando...' : 'Sin ocasión activa'}
                </button>
              </div>
              <span>
                Selecciona cuál ocasión quedará visible en la página de catálogo
                o deja el catálogo sin ocasión activa.
              </span>
            </div>

            {isLoading ? (
              <div className="catalog-settings__empty-preview">
                Cargando ocasiones...
              </div>
            ) : (
              <div className="catalog-settings__form">
                <Autocomplete
                  options={occasions}
                  getOptionLabel={getOccasionLabel}
                  getOptionSelected={(option, value) =>
                    Boolean(option) &&
                    Boolean(value) &&
                    option.id === value.id
                  }
                  value={selectedOccasion}
                  onChange={(event, newValue) => {
                    setSelectedOccasionId((newValue && newValue.id) || '');
                  }}
                  fullWidth
                  clearOnEscape
                  autoHighlight
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Ocasión"
                      placeholder="Selecciona una ocasión creada"
                    />
                  )}
                />

                <div className="catalog-settings__selected-preview">
                  {renderBannerPreview(selectedOccasion)}
                  {selectedOccasion ? (
                    <div className="catalog-settings__selected-meta">
                      <strong className="catalog-settings__occasion-name">
                        {getOccasionEmoji(selectedOccasion) ? (
                          <span className="catalog-settings__emoji-symbol">
                            {getOccasionEmoji(selectedOccasion)}
                          </span>
                        ) : null}
                        <span>{selectedOccasion.name}</span>
                      </strong>
                      {selectedOccasion.isActive ? (
                        <small>Activa actualmente</small>
                      ) : null}
                    </div>
                  ) : null}
                </div>

                <div className="catalog-settings__occasion-list">
                  {occasions.length ? (
                    occasions.map((occasion) => (
                      <div
                        key={occasion.id}
                        className={`catalog-settings__occasion-item ${
                          String(selectedOccasionId) === String(occasion.id)
                            ? 'catalog-settings__occasion-item--selected'
                            : ''
                        }`}
                      >
                        <button
                          type="button"
                          className="catalog-settings__occasion-select"
                          onClick={() => setSelectedOccasionId(occasion.id)}
                        >
                          <span className="catalog-settings__occasion-thumb">
                            {renderBannerPreview(occasion)}
                          </span>
                          <span className="catalog-settings__occasion-copy">
                            <strong className="catalog-settings__occasion-name">
                              {getOccasionEmoji(occasion) ? (
                                <span className="catalog-settings__emoji-symbol">
                                  {getOccasionEmoji(occasion)}
                                </span>
                              ) : null}
                              <span>{occasion.name}</span>
                            </strong>
                            {occasion.isActive ? <small>Activa</small> : null}
                          </span>
                        </button>
                        <IconButton
                          className="catalog-settings__delete-button"
                          aria-label={`Eliminar ocasión ${occasion.name}`}
                          onClick={() => openDeleteModal(occasion)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </div>
                    ))
                  ) : (
                    <div className="catalog-settings__empty-preview">
                      Aún no hay ocasiones creadas.
                    </div>
                  )}
                </div>

                <div className="catalog-settings__actions">
                  <Button
                    onClick={activateSelectedOccasion}
                    loading={isActivating}
                    disabled={isActivating || !selectedOccasion}
                  >
                    Elegir ocasión como activa
                  </Button>
                </div>
              </div>
            )}
          </section>
        </Grid>
      </Grid>

      {activeOccasion ? (
        <div className="catalog-settings__active-note">
          Ocasión activa:{' '}
          <strong className="catalog-settings__occasion-name">
            {getOccasionEmoji(activeOccasion) ? (
              <span className="catalog-settings__emoji-symbol">
                {getOccasionEmoji(activeOccasion)}
              </span>
            ) : null}
            <span>{activeOccasion.name}</span>
          </strong>
        </div>
      ) : (
        <div className="catalog-settings__active-note">
          Sin ocasión activa configurada.
        </div>
      )}

      <Dialog
        open={Boolean(occasionToDelete)}
        onClose={closeDeleteModal}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Eliminar ocasión</DialogTitle>
        <DialogContent>
          <p className="catalog-settings__modal-copy">
            ¿Estás seguro de que quieres eliminar la ocasión{' '}
            <strong>{occasionToDelete && occasionToDelete.name}</strong>? Esta
            acción la quitará de la lista de ocasiones del catálogo y de las
            publicaciones asociadas.
          </p>
          {occasionToDelete && occasionToDelete.isActive ? (
            <p className="catalog-settings__modal-warning">
              Esta ocasión está activa actualmente. Al eliminarla, el catálogo
              quedará sin ocasión activa hasta que selecciones otra.
            </p>
          ) : null}
        </DialogContent>
        <DialogActions>
          <button
            type="button"
            className="catalog-settings__modal-secondary"
            onClick={closeDeleteModal}
            disabled={isDeleting}
          >
            Cancelar
          </button>
          <Button
            onClick={deleteSelectedOccasion}
            loading={isDeleting}
            disabled={isDeleting}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CatalogPageSettings;
